import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MathContent from "../../customComponents/MathContent";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import {
  useGetStudentQuery,
  useGetStudentInvoicesQuery,
  useGetStudentReportsQuery,
  useRemoveInvoiceItemMutation,
  useAddInvoiceEvidenceMutation,
  useRemoveInvoiceEvidenceMutation,
  useGetSchoolBankAccountQuery,
  useGetSchoolBankAccountsQuery,
  useGetStudentQuizHistoryQuery,
  useGetStudentAnalyticsQuery,
  useUnlockReportMutation,
  useGetParentAiBalanceQuery,
} from "../../../app/parentApiSlice";
import { useGetParentLeaderboardQuery } from "../../../app/leaderboardApiSlice";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import { useGetSchoolProfileQuery } from "../settings/school/schoolApiSlice";
import { downloadInvoiceReceiptPdf } from "../invoices/receiptUtils";
import UserAvatar from "../../common/UserAvatar";

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}:
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function StudentProfile({ student }) {
  const fullName = `${student.first_name || ""} ${student.last_name || ""}`.trim();

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
        <UserAvatar
          src={student.photo}
          name={fullName}
          sx={{ width: 80, height: 80, fontSize: "1.8rem" }}
        />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {fullName || "—"}
          </Typography>
          {student.reg_number && (
            <Typography variant="body2" color="text.secondary">
              Reg No: {student.reg_number}
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <InfoRow label="Gender" value={student.gender} />
          <InfoRow label="Email" value={student.email} />
          <InfoRow label="Phone" value={student.phone} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InfoRow label="Address" value={student.address} />
          <InfoRow
            label="Date of Birth"
            value={
              student.dob
                ? new Date(student.dob).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })
                : null
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

function EvidencePreviewModal({ url, open, onClose }) {
  const [iframeLoading, setIframeLoading] = useState(true);
  const googleDocsUrl = url
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
    : null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: "90vh" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        Payment Evidence
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: "hidden", position: "relative", bgcolor: "#fff" }}>
        {iframeLoading && googleDocsUrl && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.85)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {googleDocsUrl && (
          <iframe
            key={url}
            src={googleDocsUrl}
            title="Evidence Preview"
            width="100%"
            height="100%"
            style={{ border: "none" }}
            onLoad={() => setIframeLoading(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function getInvoiceFinancials(invoice) {
  const subtotal =
    invoice?.total_amount ?? invoice?.items?.reduce((sum, item) => sum + (item.amount || 0), 0) ?? 0;
  const amountPaid = Number(invoice?.amount_paid || 0);
  const amountDue = invoice?.amount_due ?? Math.max(0, subtotal - amountPaid);
  const explicitDiscount = Number(
    invoice?.discount_amount ?? invoice?.invoice_discount?.applied_amount ?? 0
  );
  const inferredDiscount = Math.max(0, subtotal - (amountDue + amountPaid));
  const discountAmount = Math.max(explicitDiscount, inferredDiscount);
  const payableTotal = Math.max(0, subtotal - discountAmount);

  return {
    subtotal,
    discountAmount,
    payableTotal,
    amountPaid,
    amountDue,
  };
}

function ParentInvoiceModal({ invoice, studentId, student, open, onClose }) {
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadFile] = useUploadFileMutation();
  const [removeInvoiceItem, { isLoading: removing }] = useRemoveInvoiceItemMutation();
  const [addInvoiceEvidence, { isLoading: submittingEvidence }] = useAddInvoiceEvidenceMutation();
  const [removeInvoiceEvidence, { isLoading: removingEvidence }] = useRemoveInvoiceEvidenceMutation();
  const { data: bankAccount } = useGetSchoolBankAccountQuery();
  const { data: additionalBankAccounts } = useGetSchoolBankAccountsQuery();
  const { data: schoolProfile } = useGetSchoolProfileQuery();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  if (!invoice) return null;

  const { subtotal, discountAmount, payableTotal, amountPaid, amountDue } =
    getInvoiceFinancials(invoice);
  const isPaid = invoice.paid;
  const isPartial = invoice.partial_paid && !invoice.paid;
  const canDownloadReceipt = isPaid || isPartial;

  const statusLabel = isPaid ? "Paid" : isPartial ? "Partially Paid" : "Unpaid";
  const statusColor = isPaid ? "success" : isPartial ? "warning" : "error";

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm("Remove this item from the invoice?")) return;
    await removeInvoiceItem({ invoiceId: invoice.id, item_id: itemId, studentId });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await uploadFile(formData).unwrap();
      const fileUrl = result.url || result.file_url || result;
      await addInvoiceEvidence({ invoiceId: invoice.id, file_url: fileUrl, note, studentId }).unwrap();
      setNote("");
    } catch (err) {
      alert("Failed to upload evidence. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadReceipt = () => {
    try {
      const invoiceForReceipt = {
        ...invoice,
        student:
          invoice.student ||
          {
            first_name: student?.first_name || "",
            last_name: student?.last_name || "",
          },
      };
      downloadInvoiceReceiptPdf(invoiceForReceipt, schoolProfile);
    } catch {
      alert("Failed to download receipt. Please try again.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">Invoice #{invoice.id}</Typography>
          <Chip label={statusLabel} color={statusColor} size="small" />
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Summary row */}
        <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Subtotal</Typography>
            <Typography fontWeight="bold">₦{subtotal.toLocaleString()}</Typography>
          </Box>
          {discountAmount > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Discount</Typography>
              <Typography fontWeight="bold" color="info.main">−₦{discountAmount.toLocaleString()}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">Payable</Typography>
            <Typography fontWeight="bold">₦{payableTotal.toLocaleString()}</Typography>
          </Box>
          {amountPaid > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Paid</Typography>
              <Typography fontWeight="bold" color="success.main">₦{amountPaid.toLocaleString()}</Typography>
            </Box>
          )}
          {(isPartial || (!isPaid && amountPaid > 0)) && (
            <Box>
              <Typography variant="caption" color="text.secondary">Balance Due</Typography>
              <Typography fontWeight="bold" color="error.main">₦{amountDue.toLocaleString()}</Typography>
            </Box>
          )}
          {invoice.term?.name && (
            <Box>
              <Typography variant="caption" color="text.secondary">Term</Typography>
              <Typography variant="body2">{invoice.term.name}</Typography>
            </Box>
          )}
          {canDownloadReceipt && (
            <Box sx={{ ml: "auto", alignSelf: "center" }}>
              <Button
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleDownloadReceipt}
              >
                Download Receipt
              </Button>
            </Box>
          )}
        </Box>

        {/* Items table */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Items</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Amount</StyledTableCell>
                <StyledTableCell>Type</StyledTableCell>
                {!isPaid && <StyledTableCell align="center">Action</StyledTableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.items?.map((item) => (
                <StyledTableRow key={item.id}>
                  <StyledTableCell>{item.name}</StyledTableCell>
                  <StyledTableCell>₦{(item.amount || 0).toLocaleString()}</StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={item.is_compulsory ? "Compulsory" : "Optional"}
                      color={item.is_compulsory ? "default" : "info"}
                      size="small"
                      variant="outlined"
                    />
                  </StyledTableCell>
                  {!isPaid && (
                    <StyledTableCell align="center">
                      {!item.is_compulsory && (
                        <Tooltip title="Remove item">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={removing}
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Evidences */}
        {invoice.evidences?.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment Evidences</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
              {invoice.evidences.map((ev) => (
                <Paper key={ev.id} variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {ev.note && <Typography variant="body2" noWrap>{ev.note}</Typography>}
                    <Typography variant="caption" color="text.secondary">
                      {ev.created_at ? new Date(ev.created_at).toLocaleDateString("en-GB") : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={ev.confirmed ? "Confirmed" : "Pending"}
                      color={ev.confirmed ? "success" : "default"}
                      size="small"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setPreviewUrl(ev.file_url)}
                      startIcon={<VisibilityOutlinedIcon />}
                    >
                      View
                    </Button>
                    {!ev.confirmed && (
                      <Tooltip title="Remove this evidence">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={removingEvidence}
                          onClick={async () => {
                            if (!window.confirm("Remove this payment evidence?")) return;
                            await removeInvoiceEvidence({ invoiceId: invoice.id, evidenceId: ev.id, studentId });
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          </>
        )}

        <EvidencePreviewModal
          url={previewUrl}
          open={!!previewUrl}
          onClose={() => setPreviewUrl(null)}
        />

        {/* Upload evidence (not available if fully paid) */}
        {!isPaid && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {invoice.evidences?.length > 0 ? "Add Another Payment Evidence" : "Upload Payment Evidence"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ flex: 1, minWidth: 180 }}
              />
              <Button
                variant="contained"
                component="label"
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <AttachFileIcon />}
                disabled={uploading || submittingEvidence}
              >
                {uploading ? "Uploading…" : "Attach File"}
                <input type="file" hidden onChange={handleFileChange} accept="image/*,.pdf" />
              </Button>
            </Box>
          </>
        )}

        {/* Bank account details when unpaid */}
        {!isPaid && bankAccount && (
          <Box sx={{ mt: 3, p: 2, bgcolor: isDark ? "#1a2a1a" : "grey.50", borderRadius: 1, border: "1px solid", borderColor: isDark ? "#2e4a2e" : "grey.300" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Pay to this Account</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {bankAccount.bank && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Bank:</Typography>
                  <Typography variant="body2" fontWeight="medium">{bankAccount.bank}</Typography>
                </Box>
              )}
              {bankAccount.account_number && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Account Number:</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ letterSpacing: 1 }}>{bankAccount.account_number}</Typography>
                </Box>
              )}
              {bankAccount.account_name && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Account Name:</Typography>
                  <Typography variant="body2">{bankAccount.account_name}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Additional (informational) bank accounts — e.g. a separate Books Account */}
        {!isPaid && additionalBankAccounts?.length > 0 && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {additionalBankAccounts.map((account) => (
              <Box
                key={account.id}
                sx={{ p: 2, bgcolor: isDark ? "#1a2a1a" : "grey.50", borderRadius: 1, border: "1px solid", borderColor: isDark ? "#2e4a2e" : "grey.300" }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {account.description || "Pay to this Account"}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Bank:</Typography>
                    <Typography variant="body2" fontWeight="medium">{account.bank_name}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Account Number:</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ letterSpacing: 1 }}>{account.account_number}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Account Name:</Typography>
                    <Typography variant="body2">{account.account_name}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Receipt section when fully paid */}
        {isPaid && (
          <Box sx={{ mt: 3, p: 2, bgcolor: isDark ? "#0d2a1a" : "success.50", borderRadius: 1, border: "1px solid", borderColor: isDark ? "#1b4a2e" : "success.300" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <ReceiptOutlinedIcon color="success" fontSize="small" />
              <Typography variant="subtitle2" color="success.dark">Payment Receipt</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Amount Paid:</Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">₦{amountPaid.toLocaleString()}</Typography>
              </Box>
              {invoice.paid_at && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Date Paid:</Typography>
                  <Typography variant="body2">{new Date(invoice.paid_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</Typography>
                </Box>
              )}
              {invoice.term?.name && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Term:</Typography>
                  <Typography variant="body2">{invoice.term.name}</Typography>
                </Box>
              )}
              {invoice.invoice_number && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Invoice #:</Typography>
                  <Typography variant="body2">{invoice.invoice_number}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InvoicesTab({ studentId, student }) {
  const { data: invoices, isLoading, error } = useGetStudentInvoicesQuery(studentId);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoices?.length) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        No invoices found.
      </Typography>
    );
  }

  const selectedInvoice = invoices.find((inv) => inv.id === selectedInvoiceId) || null;

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>Invoice #</StyledTableCell>
              <StyledTableCell>Term</StyledTableCell>
              <StyledTableCell>Amount</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell align="center">Manage</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((inv) => {
              const { payableTotal, discountAmount } = getInvoiceFinancials(inv);
              const isPaid = inv.paid;
              const isPartial = inv.partial_paid && !inv.paid;
              const statusLabel = isPaid ? "Paid" : isPartial ? "Partial" : "Unpaid";
              const statusColor = isPaid ? "success" : isPartial ? "warning" : "error";
              return (
                <StyledTableRow key={inv.id} onClick={() => setSelectedInvoiceId(inv.id)} sx={{ cursor: "pointer" }}>
                  <StyledTableCell>{inv.invoice_number || inv.id}</StyledTableCell>
                  <StyledTableCell>{inv.term?.name || "—"}</StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2">₦{(payableTotal || 0).toLocaleString()}</Typography>
                    {discountAmount > 0 && (
                      <Typography variant="caption" color="info.main">
                        Discount: −₦{discountAmount.toLocaleString()}
                      </Typography>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip label={statusLabel} color={statusColor} size="small" />
                  </StyledTableCell>
                  <StyledTableCell>
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString("en-GB") : "—"}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Tooltip title="Manage invoice">
                      <IconButton size="small" onClick={() => setSelectedInvoiceId(inv.id)}>
                        <ReceiptOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ParentInvoiceModal
        invoice={selectedInvoice}
        studentId={studentId}
        student={student}
        open={!!selectedInvoice}
        onClose={() => setSelectedInvoiceId(null)}
      />
    </>
  );
}

function ReportPreviewModal({ url, open, onClose }) {
  const [iframeLoading, setIframeLoading] = useState(true);
  const googleDocsUrl = url
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: "90vh" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        Report Preview
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: "hidden", position: "relative" }}>
        {iframeLoading && googleDocsUrl && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: (theme) => theme.palette.mode === "dark" ? "rgba(18,18,18,0.85)" : "rgba(255,255,255,0.85)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {googleDocsUrl && (
          <iframe
            key={url}
            src={googleDocsUrl}
            title="Report Preview"
            width="100%"
            height="100%"
            style={{ border: "none", backgroundColor: "#fff" }}
            onLoad={() => setIframeLoading(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReportsTab({ studentId }) {
  const { data: reports, isLoading, error } = useGetStudentReportsQuery(studentId);
  const [unlockReport, { isLoading: isUnlocking }] = useUnlockReportMutation();
  const { data: balanceData } = useGetParentAiBalanceQuery();
  const [unlockError, setUnlockError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handlePreview = (url) => {
    if (isMobile) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      setPreviewUrl(url);
    }
  };

  const handleUnlock = async (report) => {
    setUnlockError(null);
    try {
      await unlockReport({ report_id: report.id, studentId }).unwrap();
    } catch (err) {
      const msg = err?.data?.error || "Failed to unlock report";
      setUnlockError(msg);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !reports?.length) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        No reports available yet.
      </Typography>
    );
  }

  const balance = balanceData?.balance ?? null;

  return (
    <>
    {unlockError && (
      <Box sx={{ mb: 1, p: 1, bgcolor: "error.light", borderRadius: 1 }}>
        <Typography variant="body2" color="error.contrastText">
          {unlockError === "insufficient_credits"
            ? `Insufficient credits. Current balance: ${balance ?? "—"} credits. Top up credits to unlock reports.`
            : unlockError}
        </Typography>
      </Box>
    )}
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <StyledTableCell>Term</StyledTableCell>
            <StyledTableCell>Session</StyledTableCell>
            <StyledTableCell>Class</StyledTableCell>
            <StyledTableCell>Generated</StyledTableCell>
            <StyledTableCell align="center">Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reports.map((report) => (
            <StyledTableRow key={report.id} onClick={() => !report.locked && report.file_url && handlePreview(report.file_url)} sx={{ cursor: !report.locked && report.file_url ? "pointer" : "default" }}>
              <StyledTableCell>{report.term?.name || "—"}</StyledTableCell>
              <StyledTableCell>
                {report.session?.name || "—"}
              </StyledTableCell>
              <StyledTableCell>
                {report.classroom?.name || "—"}
              </StyledTableCell>
              <StyledTableCell>
                {report.created_at
                  ? new Date(report.created_at).toLocaleDateString("en-GB")
                  : "—"}
              </StyledTableCell>
              <StyledTableCell align="center">
                {report.locked ? (
                  <Tooltip title={`Unlock for ${report.credits_required} credits`}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<LockOutlinedIcon fontSize="small" />}
                      onClick={(e) => { e.stopPropagation(); handleUnlock(report); }}
                      disabled={isUnlocking}
                    >
                      {report.credits_required} credits
                    </Button>
                  </Tooltip>
                ) : report.file_url ? (
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                    <Tooltip title="Preview report">
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(report.file_url)}
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download report">
                      <IconButton
                        size="small"
                        component="a"
                        href={report.file_url}
                        download
                      >
                        <DownloadOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  "—"
                )}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

      <ReportPreviewModal
        url={previewUrl}
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </>
  );
}

function QuizSolutionsModal({ quiz, open, onClose }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  if (!quiz) return null;

  const answers = quiz.answers || [];

  let correctCount = 0;
  let wrongCount = 0;
  answers.forEach((a) => {
    const chosen = a.question?.options?.find((o) => o.id === a.option_id);
    if (chosen?.is_answer) correctCount++;
    else wrongCount++;
  });
  const totalAnswered = correctCount + wrongCount;
  const scorePercent = totalAnswered > 0 ? Math.round((correctCount / totalAnswered) * 100) : 0;
  const quizTitle = quiz.subject
    ? `${quiz.subject}${quiz.type ? ` — ${quiz.type}` : ""}`
    : quiz.type || "Quiz";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ textTransform: "capitalize" }}>{quizTitle}</Typography>
          {quiz.level && (
            <Typography variant="body2" color="text.secondary">{quiz.level}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Score summary */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3, flexWrap: "wrap" }}>
          <Box sx={{ textAlign: "center", minWidth: 64 }}>
            <Typography sx={{ fontSize: "36px", fontWeight: 800, color: scorePercent >= 50 ? "#27AE60" : "#E02424", lineHeight: 1 }}>
              {scorePercent}%
            </Typography>
            <Typography variant="caption" color="text.secondary">Score</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: isDark ? "#1b3a27" : "#F0FFF4", px: 2, py: 1, borderRadius: 2 }}>
              <CheckCircleIcon sx={{ color: "#27AE60", fontSize: 18 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#27AE60", lineHeight: 1 }}>{correctCount}</Typography>
                <Typography variant="caption" color="text.secondary">Correct</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: isDark ? "#3a1b1b" : "#FFF5F5", px: 2, py: 1, borderRadius: 2 }}>
              <CancelIcon sx={{ color: "#E02424", fontSize: 18 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#E02424", lineHeight: 1 }}>{wrongCount}</Typography>
                <Typography variant="caption" color="text.secondary">Wrong</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: isDark ? "#2a2a2a" : "#F5F5F5", px: 2, py: 1, borderRadius: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, lineHeight: 1 }}>{totalAnswered}</Typography>
                <Typography variant="caption" color="text.secondary">Answered</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {answers.length === 0 && (
          <Typography color="text.secondary" variant="body2">No answers submitted yet.</Typography>
        )}

        {/* Per-question breakdown */}
        {answers.map((answer, i) => {
          const question = answer.question;
          const chosenOptionId = answer.option_id;
          const chosenOption = question?.options?.find((o) => o.id === chosenOptionId);
          const isCorrect = chosenOption?.is_answer === true;

          return (
            <Box
              key={answer.question_id || i}
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid",
                borderColor: isCorrect ? "#27AE60" : "#E02424",
                borderRadius: 2,
                borderLeft: "4px solid",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
                <Box sx={{ bgcolor: isCorrect ? "#27AE60" : "#E02424", color: "#fff", borderRadius: "50%", minWidth: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                  {i + 1}
                </Box>
                <Typography sx={{ flex: 1, fontSize: 15 }}>
                  <MathContent html={question?.body} />
                </Typography>
                {isCorrect ? <CheckCircleIcon sx={{ color: "#27AE60", flexShrink: 0, mt: 0.25 }} /> : <CancelIcon sx={{ color: "#E02424", flexShrink: 0, mt: 0.25 }} />}
              </Box>
              <Grid container spacing={1}>
                {question?.options?.map((option) => {
                  const isStudentChoice = option.id === chosenOptionId;
                  const isCorrectAnswer = option.is_answer === true;
                  let borderColor = isDark ? "#444" : "#E0E0E0";
                  let bgcolor = "transparent";
                  let color = "inherit";
                  let trailingIcon = null;
                  if (isStudentChoice && isCorrectAnswer) {
                    borderColor = "#27AE60"; bgcolor = isDark ? "#1b3a27" : "#F0FFF4"; color = "#27AE60";
                    trailingIcon = <CheckCircleIcon sx={{ color: "#27AE60", fontSize: 16, flexShrink: 0 }} />;
                  } else if (isStudentChoice && !isCorrectAnswer) {
                    borderColor = "#E02424"; bgcolor = isDark ? "#3a1b1b" : "#FFF5F5"; color = "#E02424";
                    trailingIcon = <CancelIcon sx={{ color: "#E02424", fontSize: 16, flexShrink: 0 }} />;
                  } else if (!isStudentChoice && isCorrectAnswer) {
                    borderColor = "#27AE60"; color = "#27AE60";
                    trailingIcon = <CheckCircleIcon sx={{ color: "#27AE60", fontSize: 16, flexShrink: 0 }} />;
                  }
                  return (
                    <Grid item xs={12} sm={6} key={option.id}>
                      <Box sx={{ border: "1px solid", borderColor, borderRadius: 2, p: 1.5, bgcolor, color, display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
                        <Box sx={{ flex: 1 }}><MathContent html={option.body} /></Box>
                        {trailingIcon}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })}
      </DialogContent>
    </Dialog>
  );
}

const PER_PAGE = 10;

function PracticeTab({ studentId }) {
  const [page, setPage] = useState(0); // MUI is 0-based
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  const { data, isLoading, isFetching, error } = useGetStudentQuizHistoryQuery({
    studentId,
    page: page + 1,
    perPage: PER_PAGE,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || (!isLoading && !data?.quizzes?.length && page === 0)) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        No quiz activity found yet.
      </Typography>
    );
  }

  const quizzes = data?.quizzes || [];
  const total = data?.pagination?.total ?? data?.total ?? 0;
  const classQuizzes = quizzes.filter((q) => !!q.school_id);
  const games = quizzes.filter((q) => !q.school_id);

  const QuizTable = ({ quizzes: rows }) => (
    <TableContainer component={Paper} sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <StyledTableCell>Quiz</StyledTableCell>
            <StyledTableCell>Class</StyledTableCell>
            <StyledTableCell>Score</StyledTableCell>
            <StyledTableCell>Date</StyledTableCell>
            <StyledTableCell align="center">Solutions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((quiz) => {
            const title = quiz.subject
              ? `${quiz.subject}${quiz.type ? ` (${quiz.type})` : ""}`
              : quiz.type || "Quiz";
            const total = quiz.questions?.length || quiz.answer_count || 0;
            const correct = quiz.correct_count ?? 0;
            const scoreColor = !quiz.submitted ? "default" : correct / total >= 0.5 ? "success" : "error";
            return (
              <StyledTableRow key={quiz.id}>
                <StyledTableCell sx={{ textTransform: "capitalize" }}>{title}</StyledTableCell>
                <StyledTableCell>{quiz.level || "—"}</StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={quiz.submitted ? `${correct} / ${total}` : "Not submitted"}
                    color={scoreColor}
                    size="small"
                  />
                </StyledTableCell>
                <StyledTableCell>
                  {quiz.created_at ? new Date(quiz.created_at).toLocaleDateString("en-GB") : "—"}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {quiz.submitted ? (
                    <Tooltip title="View solutions">
                      <IconButton size="small" onClick={() => setSelectedQuiz(quiz)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : "—"}
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ mt: 2, opacity: isFetching ? 0.6 : 1, transition: "opacity 0.2s" }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Class Quizzes
      </Typography>
      {classQuizzes.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
          No class quizzes on this page.
        </Typography>
      ) : (
        <Box sx={{ mb: 3 }}>
          <QuizTable quizzes={classQuizzes} />
        </Box>
      )}

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Games
      </Typography>
      {games.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No games on this page.
        </Typography>
      ) : (
        <QuizTable quizzes={games} />
      )}

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={PER_PAGE}
        rowsPerPageOptions={[PER_PAGE]}
        sx={{ mt: 1 }}
      />

      <QuizSolutionsModal
        quiz={selectedQuiz}
        open={!!selectedQuiz}
        onClose={() => setSelectedQuiz(null)}
      />
    </Box>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────
function scoreColor(score) {
  if (score >= 70) return "#27AE60";
  if (score >= 45) return "#F59E0B";
  return "#E02424";
}

function StatCard({ label, value, sub, color }) {
  return (
    <Paper sx={{ p: 2.5, flex: 1, minWidth: 140, textAlign: "center" }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        sx={{ fontSize: 28, fontWeight: 800, color: color || "text.primary", lineHeight: 1.1, mt: 0.5 }}
      >
        {value ?? "—"}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
      {children}
    </Typography>
  );
}

function AnalyticsTab({ studentId }) {
  const [range, setRange] = useState("all");
  const { data, isLoading, isFetching } = useGetStudentAnalyticsQuery({ studentId, range });

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;
  }

  const noData = !data || data.summary?.total_quizzes === 0;

  const { summary = {}, subject_performance = [], type_breakdown = [], weekly_trend = [], activity_by_week = [] } = data || {};

  // Keep week labels short for the chart axis
  const shortWeek = (w) => {
    if (!w || w === "Unknown") return w;
    const [yr, wk] = w.split("-W");
    return `W${wk} '${String(yr).slice(2)}`;
  };

  const trendData  = weekly_trend.map((d) => ({ ...d, week: shortWeek(d.week) }));
  const actData    = activity_by_week.map((d) => ({ ...d, week: shortWeek(d.week) }));

  return (
    <Box sx={{ mt: 2, opacity: isFetching ? 0.65 : 1, transition: "opacity 0.2s" }}>
      {/* Time range toggle */}
      <ToggleButtonGroup
        value={range}
        exclusive
        onChange={(_, v) => v && setRange(v)}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="30d">Last 30 days</ToggleButton>
        <ToggleButton value="90d">Last 3 months</ToggleButton>
        <ToggleButton value="all">All time</ToggleButton>
      </ToggleButtonGroup>

      {/* Empty state */}
      {noData && (
        <Box sx={{ textAlign: "center", mt: 6, color: "text.secondary" }}>
          <Typography>No quiz data available for this period.</Typography>
        </Box>
      )}

      {!noData && (
        <>
          {/* ── Stat cards ── */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <StatCard label="Total Quizzes" value={summary.total_quizzes} />
            <StatCard
              label="Avg Score"
              value={`${summary.avg_score ?? 0}%`}
              color={scoreColor(summary.avg_score ?? 0)}
            />
            <StatCard
              label="Best Subject"
              value={summary.best_subject || "—"}
              color="#27AE60"
              sub={summary.best_subject ? `${(subject_performance[0]?.avg_score ?? 0)}% avg` : null}
            />
            <StatCard
              label="Needs Work"
              value={summary.worst_subject || "—"}
              color={summary.worst_subject ? "#E02424" : undefined}
              sub={summary.worst_subject ? `${(subject_performance[subject_performance.length - 1]?.avg_score ?? 0)}% avg` : null}
            />
          </Box>

          {/* ── Score trend ── */}
          {trendData.length > 1 && (
            <>
              <SectionTitle>Score Trend</SectionTitle>
              <Paper sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v) => [`${v}%`, "Avg score"]} />
                    <Line
                      type="monotone"
                      dataKey="avg_score"
                      stroke="#3B82F6"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: "#3B82F6" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </>
          )}

          {/* ── Subject performance ── */}
          {subject_performance.length > 0 && (
            <>
              <SectionTitle>Performance by Subject</SectionTitle>
              <Paper sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={Math.max(180, subject_performance.length * 44)}>
                  <BarChart
                    data={subject_performance}
                    layout="vertical"
                    margin={{ top: 4, right: 40, left: 0, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="subject" width={110} tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v, n, p) => [`${v}%`, "Avg score"]} />
                    <Bar dataKey="avg_score" radius={[0, 4, 4, 0]} maxBarSize={28}>
                      {subject_performance.map((entry) => (
                        <Cell key={entry.subject} fill={scoreColor(entry.avg_score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </>
          )}

          {/* ── Quiz type breakdown ── */}
          {type_breakdown.length > 0 && (
            <>
              <SectionTitle>Score by Quiz Type</SectionTitle>
              <Paper sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={type_breakdown} margin={{ top: 10, right: 20, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v) => [`${v}%`, "Avg score"]} />
                    <Bar dataKey="avg_score" radius={[4, 4, 0, 0]} maxBarSize={40}>
                      {type_breakdown.map((entry) => (
                        <Cell key={entry.type} fill={scoreColor(entry.avg_score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </>
          )}

          {/* ── Weekly activity ── */}
          {actData.length > 1 && (
            <>
              <SectionTitle>Weekly Activity (Quizzes Taken)</SectionTitle>
              <Paper sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={actData} margin={{ top: 4, right: 20, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v) => [v, "Quizzes"]} />
                    <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </>
          )}
        </>
      )}
    </Box>
  );
}

function LeaderboardTab({ studentId }) {
  const { data, isLoading } = useGetParentLeaderboardQuery(studentId);

  if (isLoading) {
    return (
      <Box>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  const quizRanking = data?.quiz_ranking || [];
  const academicRanking = data?.academic_ranking || [];
  const weekLabel = data?.week_label;
  const classroomName = data?.classroom_name;

  if (!classroomName) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <EmojiEventsIcon sx={{ fontSize: "4rem", color: "#e0e0e0", mb: 1 }} />
        <Typography color="text.secondary">
          No classroom enrollment found for this student.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
        Classroom: <strong>{classroomName}</strong>
        {weekLabel && (
          <> &nbsp;·&nbsp; Week {weekLabel}</>
        )}
      </Typography>

      {/* Quiz Rankings */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <EmojiEventsIcon sx={{ color: "#F59E0B" }} /> Quiz Rankings
      </Typography>
      {quizRanking.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          No quiz entries this week.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#FAFAFA" }}>
                <TableCell sx={{ fontWeight: 700, width: 70 }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Points</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Questions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quizRanking.map((row) => (
                <TableRow
                  key={row.position}
                  sx={{
                    bgcolor: row.is_me
                      ? (theme) => theme.palette.mode === "dark" ? "#2e2a1a" : "#FFF8E1"
                      : "inherit",
                  }}
                >
                  <TableCell>
                    {row.position <= 3 ? (
                      <EmojiEventsIcon
                        sx={{ color: ["#FFD700", "#C0C0C0", "#CD7F32"][row.position - 1], fontSize: "1.3rem", verticalAlign: "middle" }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                        #{row.position}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: row.is_me ? 700 : 400 }}>
                      {row.display_name}
                      {row.is_me && (
                        <Chip
                          label="Your child"
                          size="small"
                          sx={{ ml: 1, height: 18, fontSize: "0.65rem", bgcolor: "#F59E0B", color: "#fff" }}
                        />
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.points.toFixed(1)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {row.questions}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Academic Rankings */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <SchoolOutlinedIcon sx={{ color: "#4CAF50" }} /> Academic Rankings
      </Typography>
      {academicRanking.length === 0 ? (
        <Typography color="text.secondary">No scores recorded for this term yet.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#FAFAFA" }}>
                <TableCell sx={{ fontWeight: 700, width: 70 }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {academicRanking.map((row) => (
                <TableRow
                  key={row.position}
                  sx={{
                    bgcolor: row.is_me
                      ? (theme) => theme.palette.mode === "dark" ? "#1a2e1a" : "#F1FFF1"
                      : "inherit",
                  }}
                >
                  <TableCell>
                    {row.position <= 3 ? (
                      <EmojiEventsIcon
                        sx={{ color: ["#FFD700", "#C0C0C0", "#CD7F32"][row.position - 1], fontSize: "1.3rem", verticalAlign: "middle" }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                        #{row.position}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: row.is_me ? 700 : 400 }}>
                      {row.display_name}
                      {row.is_me && (
                        <Chip
                          label="Your child"
                          size="small"
                          sx={{ ml: 1, height: 18, fontSize: "0.65rem", bgcolor: "#4CAF50", color: "#fff" }}
                        />
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.total_score}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function ParentStudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const { data: student, isLoading, error } = useGetStudentQuery(studentId);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !student) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Student not found.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/parent")}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/parent")}
        sx={{ mb: 2 }}
      >
        Back to My Children
      </Button>

      <StudentProfile student={student} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="Reports" />
        <Tab label="Invoices" />
        <Tab label="Quizzes & Games" />
        <Tab label="Analytics" />
        <Tab label="Leaderboard" />
      </Tabs>

      {tab === 0 && <ReportsTab studentId={studentId} />}
      {tab === 1 && <InvoicesTab studentId={studentId} student={student} />}
      {tab === 2 && <PracticeTab studentId={studentId} />}
      {tab === 3 && <AnalyticsTab studentId={studentId} />}
      {tab === 4 && <LeaderboardTab studentId={studentId} />}
    </Box>
  );
}

export default ParentStudentDetail;
